import './DetectedApplicationCard.scss';

import { AppDisplay } from '@ui/applications';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { DetectedApplicationActions } from './DetectedApplicationActions';
import { FullUserBoundary } from '@ui/general';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { SonarText } from '@ui/text';

import { Col, Row, Tooltip } from 'antd';
import { COPY_SPECIFIC, DATA_SECURITY_DASHBOARD } from '@sonar-software/sonar-ts-constants';
import {
	DataSecurityApplicationStatus,
	DetectedAppObjects,
	FeatureFlag,
	getDataSecurityDetectedApplicationById
} from '@sonar-software/sonar-core';
import { Else, If, Then, When } from 'react-if';
import { EyeInvisibleOutlined, WarningTwoTone } from '@ant-design/icons';
import React, { useCallback, useState } from 'react';
import { useFeatureFlag, useOnMount } from '@hooks';

interface DetectedAppData {
	firstDetected?: Date;
	lastDetected?: Date;
	objectMoreCount: number;
	objectOverflow: boolean;
	objectsToDisplay: DetectedAppObjects[];
	status: string,
	userMoreCount: number;
	userOverflow: boolean;
	usersToDisplay: string[];
}

const defaultData: DetectedAppData = {
	objectMoreCount: 0,
	objectOverflow: false,
	objectsToDisplay: [],
	status: '',
	userMoreCount: 0,
	userOverflow: false,
	usersToDisplay: []
};

const detectedStatus = DataSecurityApplicationStatus[ DataSecurityApplicationStatus.Detected ].toLowerCase();
const hiddenStatus = DataSecurityApplicationStatus[ DataSecurityApplicationStatus.Deleted ].toLowerCase();

export interface DetectedApplicationCardProps {
    applicationId: number;
}

export const DetectedApplicationCard: React.FC<DetectedApplicationCardProps> = ({ applicationId }) => {
	const [ data, setData ] = useState<DetectedAppData>(defaultData);
	const [ loading, setLoading ] = useState<boolean>(true);

	const showCounts = useFeatureFlag(FeatureFlag.DataSecurityDashboard);

	const loadData = useCallback(async() => {
		setLoading(true);
		const results = await getDataSecurityDetectedApplicationById({ applicationId });

		if (!results.data) {
			return;
		}

		const userOverflow = results.data.users.length > 3;
		const objectOverflow = results.data.totalObjects > results.data.objects.length;

		setData({
			firstDetected: results.data.firstDetected,
			lastDetected: results.data.lastDetected,
			objectMoreCount: objectOverflow ? results.data.totalObjects - results.data.objects.length + 1 : 0,
			objectOverflow,
			objectsToDisplay:
				objectOverflow ?
					results.data.objects.slice(0, results.data.objects.length - 1) :
					results.data.objects,
			status: results.data.status,
			userMoreCount: userOverflow ? results.data.users.length - 2 : 0,
			userOverflow,
			usersToDisplay: userOverflow ? results.data.users.slice(0, 2) : results.data.users
		});

		setLoading(false);
	}, [ applicationId, setLoading ]);

	useOnMount(() => {
		loadData();
	});

	const handleStatusChange = useCallback((status: string) => {
		setData((current) => ({
			...current,
			status
		}));
	}, []);

	return (
		<div
			className={ classNames('detected-app-card', { 'hidden-styling': data.status === hiddenStatus }) }
			data-cy={ `detected-app-card-${applicationId}` }>
			<div className='detected-app-header'>
				<Row
					align='middle'
					className='header-first-row'
					justify='space-between'>
					<Col>
						<If
							condition={ data.status === detectedStatus }>
							<Then>
								<WarningTwoTone
									style={ { fontSize: '20px' } }
									twoToneColor='#FAAD14' />
							</Then>
							<Else>
								<EyeInvisibleOutlined
									style={ { fontSize: '20px' } } />
							</Else>
						</If>
					</Col>
					<Col>
						<FullUserBoundary>
							<DetectedApplicationActions
								applicationId={ applicationId }
								onStatusChange={ handleStatusChange }
								status={ data.status } />
						</FullUserBoundary>
					</Col>
				</Row>
				<Row justify='center'>
					<AppDisplay appId={ applicationId } />
				</Row>
			</div>
			<div className='detected-app-body'>
				<LoadingSpinner spinning={ loading }>
					<Row className='flex-column'>
						<SonarText weight='semibold'> { COPY_SPECIFIC.USERS } </SonarText>
						{ data.usersToDisplay?.map((email) =>
							<Tooltip
								key={ email }
								mouseEnterDelay={ 0.5 }
								title={ email }>
								<SonarText
									className='user'
									key={ email }> { email }
								</SonarText>
							</Tooltip>) }
						<When condition={ data.userOverflow }>
							<SonarText>
								{ DATA_SECURITY_DASHBOARD.PLUS_MORE(data.userMoreCount) }
							</SonarText>
						</When>
					</Row>
					<Row
						align='middle'
						justify='space-between'>
						<Col className='flex-column'>
							<SonarText weight='semibold'>{ DATA_SECURITY_DASHBOARD.FIRST_DETECTED }</SonarText>
							<SonarText>{ dayjs(data?.firstDetected).format('MMM Do, YYYY') }</SonarText>
						</Col>
						<Col className='flex-column'>
							<SonarText weight='semibold'>{ DATA_SECURITY_DASHBOARD.LAST_DETECTED }</SonarText>
							<SonarText>{ dayjs(data?.lastDetected).format('MMM Do, YYYY') }</SonarText>
						</Col>
					</Row>
					<When condition={ showCounts }>
						<Row className='flex-column'>
							<Row
								align='middle'
								justify='space-between'>
								<Col><SonarText weight='semibold'>{ COPY_SPECIFIC.OBJECTS }</SonarText></Col>
								<Col><SonarText weight='semibold'>{ COPY_SPECIFIC.FIELD_COUNT }</SonarText></Col>
							</Row>
							{ data.objectsToDisplay?.map((object) =>
								<Row
									align='middle'
									justify='space-between'
									key={ object.name }>
									<Col>{ object.name }</Col>
									<Col>{ object.count }</Col>
								</Row>) }
							<When condition={ data.objectOverflow }>
								<SonarText>
									{ DATA_SECURITY_DASHBOARD.PLUS_MORE(data.objectMoreCount) }
								</SonarText>
							</When>
						</Row>
					</When>
				</LoadingSpinner>
			</div>
		</div>
	);
};
